import React, { useEffect, useState } from 'react';
import { Table, Input, Button, Select, Space, Tag } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { customerService, Customer } from '../../services/customerService';
import './Customers.scss';

const { Option } = Select;

const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await customerService.getCustomers({
        page,
        limit,
        search: searchText || undefined,
        status: statusFilter,
      });
      setCustomers(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const handleSearch = () => {
    setPage(1);
    fetchCustomers();
  };

  const handleReset = () => {
    setSearchText('');
    setStatusFilter(undefined);
    setPage(1);
    fetchCustomers();
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'firstName',
      key: 'name',
      render: (_: any, record: Customer) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : status === 'SUSPENDED' ? 'red' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'KYC Level',
      dataIndex: 'kycLevel',
      key: 'kycLevel',
      render: (level: string) => level || 'ANONYMOUS',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: Customer) => (
        <Button type="link" onClick={() => navigate(`/customers/${record.id}`)}>
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div className="customers-page">
      <h2>Customers</h2>
      <div className="filters-section">
        <Space size="middle">
          <Input
            placeholder="Search by name, phone, or email..."
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
          />
          <Select
            placeholder="Filter by status"
            style={{ width: 150 }}
            allowClear
            value={statusFilter}
            onChange={setStatusFilter}
          >
            <Option value="ACTIVE">Active</Option>
            <Option value="SUSPENDED">Suspended</Option>
            <Option value="CLOSED">Closed</Option>
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            Search
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Reset
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={customers}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} customers`,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setLimit(newPageSize || 20);
          },
        }}
      />
    </div>
  );
};

export default CustomersPage;
